import styles from "./style.module.scss";
import { h } from "preact";
import { useMemo } from "preact/hooks";
import { Text } from "preact-i18n";
import CloseIcon from "icons/CloseIcon";
import type MvArea from "models/MvArea";
import type MvSelectedGroup from "models/MvSelectedGroup";
import {
    createSelectionSummary,
    updateZipCodeSelection,
} from "services/selectionGroups";
import PostcodeGroup from "./PostcodeGroup";
import SelectionFooter from "./SelectionFooter";
export type { AddressGroup } from "services/selectionGroups";

export interface AsidePanelProps {
    isOpen: boolean;
    areas: MvArea[];
    isEditable: boolean;
    addressLimit?: number;
    addressMinimum?: number;
    zipCodeAreaMinCount?: number;
    onClose?(): void;
    onConfirm(): void;
    showFullZipCode?: boolean;
    selectedGroups: MvSelectedGroup[];
    onGroupsChange(
        groups:
            | MvSelectedGroup[]
            | ((previous: MvSelectedGroup[]) => MvSelectedGroup[]),
    ): void;
}

const AsidePanel = ({
    isOpen,
    areas,
    isEditable,
    addressLimit,
    addressMinimum,
    zipCodeAreaMinCount,
    onClose,
    onConfirm,
    showFullZipCode = false,
    selectedGroups,
    onGroupsChange,
}: AsidePanelProps) => {
    const summary = useMemo(
        () =>
            createSelectionSummary(
                areas,
                selectedGroups,
                zipCodeAreaMinCount,
                addressMinimum,
                addressLimit,
            ),
        [areas, selectedGroups, zipCodeAreaMinCount, addressLimit],
    );

    const { addressGroups, addressCount, isConfirmDisabled } = summary;
    const toggleGroup = (
        id: string,
        postcode: string,
        street: string,
        checked: boolean,
    ) => {
        onGroupsChange((previous) =>
            updateZipCodeSelection(previous, id, postcode, (streets) =>
                checked
                    ? [...streets.filter((name) => name !== street), street]
                    : streets.filter((name) => name !== street),
            ),
        );
    };
    const toggleCheckAll = (id: string, postcode: string, checked: boolean) => {
        const streets = Object.keys(
            addressGroups.find((group) => group.id === id)
                ?.groupedByZipCodeAndStreetName[postcode] ?? {},
        );
        onGroupsChange((previous) =>
            updateZipCodeSelection(previous, id, postcode, () =>
                checked ? streets : [],
            ),
        );
    };
    return (
        <aside className={`${styles.mvAside} ${isOpen ? styles.open : ""}`}>
            <div className={styles.mvAsidePanelHeader}>
                <h2>
                    <Text id="slideInPanel.header.title">Addresses</Text>
                </h2>
                {!isEditable && (
                    <button
                        type="button"
                        onClick={onClose}
                        className={styles.mvPanelCloseButton}
                    >
                        <CloseIcon
                            fill="currentColor"
                            className={styles.mvPanelCloseIcon}
                        />
                    </button>
                )}
            </div>
            <div className={styles.mvAsidePanelBody}>
                {addressGroups.length > 0 ? (
                    addressGroups.map((group) => (
                        <div
                            className={styles.mvAsidePanelAddressGroup}
                            key={group.id}
                        >
                            <h3>
                                {group.result?.straatnaam}{" "}
                                {group.result?.huisnr}
                                {group.result?.huisnr_bag_letter
                                    ? `${group.result.huisnr_bag_letter}${group.result.huisnr_bag_toevoeging || ""}`
                                    : ""}
                                , {group.result?.postcode}{" "}
                                {group.result?.plaatsnaam}
                            </h3>
                            {Object.keys(
                                group.groupedByZipCodeAndStreetName,
                            ).map((postcode) => (
                                <PostcodeGroup
                                    key={postcode}
                                    group={group}
                                    zipCodeAreaKey={postcode}
                                    isEditable={isEditable}
                                    showFullZipCode={showFullZipCode}
                                    selectedGroups={selectedGroups}
                                    summary={summary}
                                    zipCodeAreaMinCount={zipCodeAreaMinCount}
                                    toggleGroup={toggleGroup}
                                    toggleCheckAll={toggleCheckAll}
                                />
                            ))}
                        </div>
                    ))
                ) : (
                    <p>
                        <Text id="slideInPanel.content.noSelection">
                            Please select some addresses in the previous step
                        </Text>
                    </p>
                )}
            </div>
            <SelectionFooter
                summary={summary}
                addressLimit={addressLimit}
                addressMinimum={addressMinimum}
                addressCount={addressCount}
                isEditable={isEditable}
                disabled={isConfirmDisabled()}
                onConfirm={onConfirm}
                onClose={onClose}
            />
        </aside>
    );
};
export default AsidePanel;
